import React, { useState } from "react";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, ShieldCheck, Globe } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { company, services } from "../mock";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", businessName: "", email: "", phone: "", service: "", message: "", consent: false, website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.consent) {
      toast({ title: "Please complete required fields", description: "Name, email, message and consent are required." });
      return;
    }
    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          business: form.businessName,
          email: form.email,
          phone: form.phone,
          service: form.service,
          message: form.message,
          consent: form.consent,
          website: form.website, // honeypot
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Submission failed");
      }
      setSubmitted(true);
      toast({
        title: "Enquiry sent",
        description: `Thanks ${form.name.split(" ")[0]} — we will respond within one business day.`,
      });
    } catch (err) {
      toast({
        title: "We could not send your message",
        description: `Please try again or email us directly at ${company.email}.`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <PageHero
        eyebrow="Contact"
        title="Book a Free 15 Minute Discovery Call"
        subtitle="Discuss your registration pathway, documentation needs, internal audit requirements, mid term audit readiness or corrective action support with Apex Quality Consultancy."
        breadcrumb={[{ label: "Contact" }]}
      />

      <section className="py-20">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <SectionHeading eyebrow="Get in touch" title="We will respond within one business day" />
            <div className="space-y-4 mt-2">
              <div className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-slate-200 hover:border-teal transition" data-testid="contact-card-phone">
                <div className="w-11 h-11 rounded-xl bg-teal-soft text-teal flex items-center justify-center shrink-0">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</div>
                  <div className="mt-1 text-navy font-semibold flex items-center gap-2 flex-wrap">
                    <a href={`tel:${company.phoneRaw}`} className="hover:text-teal transition" data-testid="contact-phone-1">{company.phone}</a>
                    <span className="text-slate-300">|</span>
                    <a href={`tel:${company.phoneRaw2}`} className="hover:text-teal transition" data-testid="contact-phone-2">{company.phone2}</a>
                  </div>
                </div>
              </div>
              {[
                { icon: Mail, label: "Email", value: company.email, href: `mailto:${company.email}` },
                { icon: MapPin, label: "Location", value: company.address },
                { icon: Globe, label: "Service area", value: company.serviceArea },
                { icon: Clock, label: "Hours", value: company.hours },
              ].map((c) => (
                <a key={c.label} href={c.href || "#"} className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-slate-200 hover:border-teal transition">
                  <div className="w-11 h-11 rounded-xl bg-teal-soft text-teal flex items-center justify-center shrink-0">
                    <c.icon size={20} />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.label}</div>
                    <div className="mt-1 text-navy font-semibold">{c.value}</div>
                  </div>
                </a>
              ))}
            </div>

            <div className="p-5 bg-teal-soft rounded-2xl border-l-4 border-teal flex items-start gap-3">
              <ShieldCheck size={20} className="text-teal shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700 leading-relaxed">
                We do not act as your independent external NDIS auditor for providers we have consulted to. Our
                consultancy services support registration preparation, internal audit, implementation and compliance
                improvement only.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <div className="w-16 h-16 rounded-2xl bg-teal-soft text-teal flex items-center justify-center mx-auto">
                  <CheckCircle2 size={32} />
                </div>
                <h3 className="mt-5 text-2xl font-bold text-navy">Thank you, {form.name.split(" ")[0] || "there"}.</h3>
                <p className="mt-3 text-slate-600">
                  Your enquiry has been received. An audit informed consultant will respond within one business day.
                </p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", businessName: "", email: "", phone: "", service: "", message: "", consent: false, website: "" }); }} className="mt-6 btn-secondary px-5 py-3 rounded-lg font-semibold text-sm" data-testid="contact-send-another">
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-slate-200 p-7 md:p-9 space-y-5 shadow-sm" data-testid="contact-form">
                <h3 className="text-navy font-bold text-xl">Send us an enquiry</h3>
                {/* Honeypot — hidden from real users, bots tend to fill it */}
                <input
                  type="text"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.website}
                  onChange={(e) => update("website", e.target.value)}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-navy font-semibold">Name *</Label>
                    <Input id="name" data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-2 h-11" placeholder="Your full name" />
                  </div>
                  <div>
                    <Label htmlFor="businessName" className="text-navy font-semibold">Business name</Label>
                    <Input id="businessName" data-testid="contact-business" value={form.businessName} onChange={(e) => update("businessName", e.target.value)} className="mt-2 h-11" placeholder="Your business name" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-navy font-semibold">Email *</Label>
                    <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-2 h-11" placeholder="you@provider.com.au" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-navy font-semibold">Phone</Label>
                    <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2 h-11" placeholder="04xx xxx xxx" />
                  </div>
                </div>

                <div>
                  <Label className="text-navy font-semibold">Service needed</Label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger className="mt-2 h-11" data-testid="contact-service-trigger"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (<SelectItem key={s.slug} value={s.slug}>{s.title}</SelectItem>))}
                      <SelectItem value="diy">NDIS DIY Compliance Pack</SelectItem>
                      <SelectItem value="done-for-you">NDIS Done for You Registration Package</SelectItem>
                      <SelectItem value="premium">NDIS Premium Audit Readiness Package</SelectItem>
                      <SelectItem value="not-sure">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-navy font-semibold">Message *</Label>
                  <Textarea id="message" data-testid="contact-message" value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-2 min-h-32" placeholder="Tell us about your registration pathway, audit timeline, modules and any specific challenges..." />
                </div>

                <div className="flex items-start gap-2.5">
                  <Checkbox id="consent" data-testid="contact-consent" checked={form.consent} onCheckedChange={(v) => update("consent", !!v)} className="mt-1" />
                  <label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                    I consent to Apex Quality Consultancy contacting me about my enquiry. I understand that
                    information submitted through this form will be handled in accordance with the Privacy Policy. *
                  </label>
                </div>

                <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-primary w-full md:w-auto px-6 py-3.5 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting ? "Sending..." : (<>Send Enquiry <Send size={16} /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
